import type { QueryClient } from '@tanstack/react-query';

import {
  commitConfirmedDashboardConfig,
  dashboardOptions,
  isDashboardConsistencyTimeoutError
} from '@/features/dashboard';
import type { DashboardConfig } from '@/features/dashboard';

import type { CreateDashboardTabParams } from './symbol-selector.api';
import { createDashboardTab } from './symbol-selector.api';

export function createDashboardTabMutationOptions(
  queryClient: QueryClient,
  onNavigate: (dashboardName: string, tabName: string) => void,
  onRollbackNavigation: () => void = () => {}
) {
  const queryKey = dashboardOptions.all();

  return {
    mutationKey: dashboardOptions.mutations.addWidget(),
    mutationFn: createDashboardTab,
    onMutate: async ({ dashboardName, tabName, payload }: CreateDashboardTabParams) => {
      await queryClient.cancelQueries({ queryKey });
      const previousCache = queryClient.getQueryData<DashboardConfig>(queryKey);

      queryClient.setQueryData<DashboardConfig>(queryKey, current => ({
        ...current,
        [dashboardName]: payload
      }));
      onNavigate(dashboardName, tabName);

      return { previousCache };
    },
    onSuccess: (dashboards: DashboardConfig) => {
      commitConfirmedDashboardConfig(queryClient, dashboards);
    },
    onError: (
      error: unknown,
      _variables: CreateDashboardTabParams,
      context?: {
        previousCache: DashboardConfig | undefined;
      }
    ) => {
      if (isDashboardConsistencyTimeoutError(error)) return;

      if (context?.previousCache) {
        queryClient.setQueryData(queryKey, context.previousCache);
      } else {
        queryClient.removeQueries({ queryKey, exact: true });
      }
      onRollbackNavigation();
    }
  };
}
